import React, { useEffect, useState } from 'react';
import { getIngredients } from '../api';
import './RecipeModal.css';

export default function RecipeModal({ meal, onClose }) {
  const [imgError, setImgError] = useState(false);
  const ingredients = getIngredients(meal);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const youtubeId = meal.strYoutube
    ? meal.strYoutube.split('v=')[1]?.split('&')[0]
    : null;

  return (
    <div className="modal-backdrop" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" role="dialog" aria-modal="true" aria-label={meal.strMeal}>
        {/* Header image */}
        <div className="modal-hero">
          {!imgError ? (
            <img
              src={meal.strMealThumb}
              alt={meal.strMeal}
              className="modal-hero-img"
              onError={() => setImgError(true)}
            />
          ) : (
            <div className="modal-hero-fallback">🍽️</div>
          )}
          <div className="modal-hero-overlay" />
          <button className="modal-close" onClick={onClose} aria-label="Close">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div className="modal-hero-info">
            <div className="modal-badges">
              {meal.strCategory && <span className="badge badge-category">{meal.strCategory}</span>}
              {meal.strArea && <span className="badge badge-area">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>
                {meal.strArea}
              </span>}
            </div>
            <h2 className="modal-title">{meal.strMeal}</h2>
          </div>
        </div>

        <div className="modal-body">
          {/* Tags */}
          {meal.strTags && (
            <div className="modal-tags">
              {meal.strTags.split(',').filter(Boolean).map(tag => (
                <span key={tag} className="modal-tag">{tag.trim()}</span>
              ))}
            </div>
          )}

          {/* Two-column layout */}
          <div className="modal-cols">
            {/* Ingredients */}
            <section className="modal-ingredients">
              <h3 className="section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z"/><path d="M12 8v4l3 3"/></svg>
                Ingredients
                <span className="ingredient-count">{ingredients.length}</span>
              </h3>
              <ul className="ingredients-list">
                {ingredients.map(({ ingredient, measure }, i) => (
                  <li key={i} className="ingredient-item">
                    <img
                      src={`https://www.themealdb.com/images/ingredients/${ingredient}-Small.png`}
                      alt={ingredient}
                      className="ingredient-img"
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <span className="ingredient-name">{ingredient}</span>
                    {measure && <span className="ingredient-measure">{measure}</span>}
                  </li>
                ))}
              </ul>
            </section>

            {/* Instructions */}
            <section className="modal-instructions">
              <h3 className="section-title">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
                Instructions
              </h3>
              <div className="instructions-steps">
                {meal.strInstructions
                  ? meal.strInstructions
                      .split(/\r?\n/)
                      .filter(line => line.trim().length > 10)
                      .map((step, i) => (
                        <div key={i} className="step">
                          <span className="step-num">{i + 1}</span>
                          <p className="step-text">{step.trim()}</p>
                        </div>
                      ))
                  : <p className="no-instructions">No instructions available.</p>
                }
              </div>
            </section>
          </div>

          {/* YouTube link */}
          {youtubeId && (
            <a
              href={meal.strYoutube}
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0C.488 3.45.029 5.804 0 12c.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0C23.512 20.55 23.971 18.196 24 12c-.029-6.185-.484-8.549-4.385-8.816zM9 16V8l8 4-8 4z"/></svg>
              Watch on YouTube
            </a>
          )}

          {/* Source */}
          {meal.strSource && (
            <a
              href={meal.strSource}
              target="_blank"
              rel="noopener noreferrer"
              className="source-link"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              View Original Source
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
